import { useContext, useMemo } from 'react';
import * as d3Scale from 'd3-scale';
import { Scale } from '../Types';
import getAxisLine from './getAxisLine';
import { getScaleBandWidth, getScaleTicks } from './getScale';
import VizContext from '../VizContext';

interface Config {
  align?: 'start' | 'end';
  count?: number;
  flip?: boolean;
  scale: Scale;
  values?: number[];
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
}

export default (config: Config) => {
  const {
    align = 'start',
    count,
    flip,
    scale,
    values,
    x0,
    x1,
    y0,
    y1,
  } = config;

  const {
    artboardBottom,
    artboardLeft,
    artboardRight,
    artboardTop,
    xDomain,
    yDomain,
  } = useContext(VizContext);

  return useMemo(() => {
    const [
      ax0,
      ax1,
      ay0,
      ay1,
      xLineDomain,
      yLineDomain,
    ] = getAxisLine(x0, x1, y0, y1, xDomain, yDomain, align);

    const xScaleLine = d3Scale
      .scaleLinear()
      .domain(xLineDomain)
      .range([artboardLeft, artboardRight]);

    const yScaleLine = d3Scale
      .scaleLinear()
      .domain(yLineDomain)
      .range([artboardBottom, artboardTop]);

    const px0 = xScaleLine(ax0);
    const px1 = xScaleLine(ax1);
    const py0 = yScaleLine(ay0);
    const py1 = yScaleLine(ay1);

    const dx = px1 - px0;
    const dy = py1 - py0;

    const r = Math.atan2(dy, dx) + (flip ? Math.PI : 0);

    const tickScaleX = scale.copy().range([px0, px1]) as Scale;
    const tickScaleY = scale.copy().range([py0, py1]) as Scale;

    const bandWidthX = getScaleBandWidth(tickScaleX) / 2;
    const bandWidthY = getScaleBandWidth(tickScaleY) / 2;

    const ticks: [number, number, number][] = (values || getScaleTicks(scale, count)).map((tick) => [
      tick,
      (tickScaleX(tick) || 0) + bandWidthX,
      (tickScaleY(tick) || 0) + bandWidthY,
    ]);

    return {
      bandWidthX,
      bandWidthY,
      px0,
      px1,
      py0,
      py1,
      r,
      ticks,
    };
  }, [
    artboardBottom,
    artboardLeft,
    artboardRight,
    artboardTop,
    xDomain,
    yDomain,

    align,
    count,
    flip,
    scale,
    values,
    x0,
    x1,
    y0,
    y1,
  ]);
};
